import { onKeyStroke } from "@vueuse/core";
import { onMounted, toValue } from "vue";

import { isPresent } from "#ce/utils/props";

/**
 * Moves focus between the digit boxes of the group: Backspace in an empty box steps back, the
 * arrow keys step either way, and typing advances to the next box. Once an arrow key moved the
 * focus, typing advances only into an empty box.
 *
 * @param {object} options
 * @param {import("vue").Ref<HTMLElement|null>} options.group - Element holding the boxes.
 * @param {import("vue").Ref<string[]>} options.digits - The digit of every box.
 * @param {import("vue").MaybeRefOrGetter<*>} options.autofocus - Focuses the first box on mount when present.
 * @returns {{
 *   boxIndex: (event: Event) => number,
 *   focusAfterTyping: (index: number, wasNextBoxEmpty: boolean) => void,
 * }}
 */
export function useOtpNavigation({ group, digits, autofocus }) {
  let isFocusUserControlled = false;

  function boxes() {
    return [...(group.value?.querySelectorAll("input") ?? [])];
  }

  function boxIndex(event) {
    return Number(event.target.dataset.index);
  }

  function focusBox(index) {
    const box = boxes()[index];

    if (!box) {
      return;
    }

    box.focus();
    box.select();
  }

  function focusAfterTyping(index, wasNextBoxEmpty) {
    if (!isFocusUserControlled || wasNextBoxEmpty) {
      focusBox(index + 1);
    }
  }

  function handleNavigationKey(event) {
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    const index = boxIndex(event);

    if (event.key === "Backspace") {
      if (digits.value[index] === "") {
        event.preventDefault();
        focusBox(index - 1);
      }

      return;
    }

    event.preventDefault();
    isFocusUserControlled = true;
    focusBox(event.key === "ArrowLeft" ? index - 1 : index + 1);
  }

  function focusFirstBoxOnAutofocus() {
    if (isPresent(toValue(autofocus))) {
      focusBox(0);
    }
  }

  onKeyStroke(["Backspace", "ArrowLeft", "ArrowRight"], handleNavigationKey, { target: group });
  onMounted(focusFirstBoxOnAutofocus);

  return { boxIndex, focusAfterTyping };
}
